/**
 * «Расписание» — экран родителя.
 *
 * Неделя сверху как навигация, день списком, добавление одной кнопкой.
 * Повторяющиеся задачи живут отдельным блоком: их правят редко, но видеть
 * их состав важно — иначе непонятно, откуда в дне взялась задача.
 */

import { useState } from 'react'

import { useFamily, useTimeZone } from '@/app/session'
import { TaskList } from '@/features/schedule/TaskList'
import { TaskSheet } from '@/features/schedule/TaskSheet'
import { WeekStrip } from '@/features/schedule/WeekStrip'
import {
  WEEKDAY_LABELS,
  useArchiveSeries,
  useDeleteTask,
  useSchedule,
  type ScheduleTask,
} from '@/shared/api/schedule'
import { familyDate, formatDayLabel } from '@/shared/format'
import { Button } from '@/shared/ui/Button'
import { Screen } from '@/shared/ui/Screen'
import { Icon } from '@/shared/ui/Icon'
import { ConfirmSheet } from '@/shared/ui/Sheet'
import { Card, Chip, Row, SectionTitle } from '@/shared/ui/primitives'
import { ErrorState, SkeletonCard } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

function weekStart(date: string): string {
  const parsed = new Date(`${date}T12:00:00Z`)
  const iso = parsed.getUTCDay() === 0 ? 7 : parsed.getUTCDay()
  parsed.setUTCDate(parsed.getUTCDate() - (iso - 1))
  return parsed.toISOString().slice(0, 10)
}

export function Schedule() {
  const family = useFamily()
  const timeZone = useTimeZone()
  const familyId = family?.id ?? 0

  const today = familyDate(new Date(), timeZone)
  const [selected, setSelected] = useState(today)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [seriesToStop, setSeriesToStop] = useState<number | null>(null)

  const start = weekStart(selected)
  const schedule = useSchedule(familyId, start, 7)

  const deleteTask = useDeleteTask(familyId, {
    onSuccess: () => toast.show('Задача удалена'),
    onError: (error) => toast.error(error.message),
  })
  const archiveSeries = useArchiveSeries(familyId, {
    onSuccess: () => {
      setSeriesToStop(null)
      toast.show('Повтор остановлен')
    },
    onError: (error) => toast.error(error.message),
  })

  if (!family) return null
  if (schedule.isLoading) {
    return (
      <Screen title="Расписание">
        <SkeletonCard lines={2} />
        <SkeletonCard lines={3} />
      </Screen>
    )
  }
  if (schedule.error) {
    return (
      <Screen title="Расписание">
        <ErrorState error={schedule.error} onRetry={() => void schedule.refetch()} />
      </Screen>
    )
  }

  const data = schedule.data!
  const dayTasks = data.tasks.filter((task: ScheduleTask) => task.date === selected)

  return (
    <Screen
      title="Расписание"
      right={
        <Button size="sm" onClick={() => setSheetOpen(true)}>
          Добавить
        </Button>
      }
    >
      <WeekStrip
        start={start}
        selected={selected}
        tasks={data.tasks}
        onSelect={setSelected}
      />

      <div>
        <SectionTitle>{formatDayLabel(selected, timeZone)}</SectionTitle>
        <TaskList
          tasks={dayTasks}
          canComplete={false}
          onDelete={(task) => deleteTask.mutate(task.id)}
          emptyTitle="На этот день задач нет"
          emptyDescription="Няня увидит задачи в своём дне и получит напоминание заранее."
          emptyAction={<Button onClick={() => setSheetOpen(true)}>Добавить задачу</Button>}
        />
      </div>

      {data.series.length > 0 && (
        <div>
          <SectionTitle>Повторяются</SectionTitle>
          <Card padded={false} className="px-4">
            {data.series.map((series, index) => (
              <div key={series.id}>
                {index > 0 && <div className="bg-line h-px" />}
                <Row
                  icon={
                    <span
                      aria-hidden
                      className="bg-surface-muted flex size-10 items-center justify-center rounded-[var(--radius-button)] text-[18px]"
                    >
                      {series.emoji}
                    </span>
                  }
                  title={series.title}
                  subtitle={`${series.time_start.slice(0, 5)} · ${series.weekdays
                    .map((day) => WEEKDAY_LABELS[day - 1])
                    .join(', ')}`}
                  right={
                    series.paused ? (
                      <Chip tone="neutral">пауза</Chip>
                    ) : (
                      <Icon name="action-chevron" size={20} className="opacity-70" />
                    )
                  }
                  onClick={() => setSeriesToStop(series.id)}
                />
              </div>
            ))}
          </Card>
        </div>
      )}

      <TaskSheet
        open={sheetOpen}
        familyId={familyId}
        date={selected}
        onClose={() => setSheetOpen(false)}
      />

      <ConfirmSheet
        open={seriesToStop !== null}
        title="Остановить повтор?"
        description="Будущие неотмеченные задачи этой серии исчезнут. Уже выполненные останутся в истории."
        confirmLabel="Остановить"
        danger
        loading={archiveSeries.isPending}
        onConfirm={() => seriesToStop && archiveSeries.mutate(seriesToStop)}
        onClose={() => setSeriesToStop(null)}
      />
    </Screen>
  )
}
